import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const { styles } = useSiteStyles();
  const companyName = styles?.navLogoText || "SellLocalHomes";
  const location = useLocation();
  // Admin/agent dashboards are behind auth already (no anonymous cookie
  // question to ask) and are full of fixed-position controls the banner
  // would otherwise sit on top of — never render it there.
  const isDashboardRoute = /^\/(admin|property-admin)(\/|$)/.test(location.pathname);

  useEffect(() => {
    // Skip the banner entirely if the visitor is signed in on any of the
    // three session types the site supports (shopper, agent/property-admin,
    // master admin). Signed-in users have already implicitly accepted the
    // ToS via the auth flow, so showing another consent banner is noise.
    let cancelled = false;
    const consent = localStorage.getItem("cookie_consent");
    if (consent) return; // already answered — hide forever
    (async () => {
      const probes = [
        `${API}/shopper/me`,
        `${API}/property-admin/me`,
        `${API}/auth/me`,
      ];
      for (const url of probes) {
        try {
          await axios.get(url, { withCredentials: true });
          if (!cancelled) localStorage.setItem("cookie_consent", "signed_in");
          return; // any success means at least one session is active
        } catch { /* not signed in on this endpoint — try the next */ }
      }
      if (!cancelled) setVisible(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const accept = () => {
    localStorage.setItem("cookie_consent", "all");
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem("cookie_consent", "essential");
    setVisible(false);
  };

  if (!visible || isDashboardRoute) return null;

  return (
    <div
      className="fixed bottom-4 left-4 z-[100] w-64 bg-white border border-border shadow-xl"
      data-testid="cookie-consent-banner"
    >
      <div className="p-3">
        <p className="text-[11px] text-muted-foreground leading-relaxed mb-2.5">
          We use cookies to run and improve {companyName}. See our{" "}
          <Link to="/privacy" className="text-primary underline">Privacy Notice</Link>.
        </p>
        <div className="flex flex-col gap-1.5">
          <button
            onClick={accept}
            className="w-full px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity"
            data-testid="cookie-consent-accept-btn"
          >
            Accept All
          </button>
          <button
            onClick={decline}
            className="w-full px-3 py-1.5 border border-border text-xs font-medium hover:bg-muted transition-colors"
            data-testid="cookie-consent-decline-btn"
          >
            Essential Only
          </button>
        </div>
      </div>
    </div>
  );
}
